/**
 * The only module that fetches the deployed build manifest.
 *
 * `/version.json` is emitted by the build and is the one signal a running tab
 * can read without a service worker's help — which is exactly the situation on
 * iOS Safari in a browser tab, in a private window, and in Playwright's WebKit
 * build.
 */

import { VERSION_MANIFEST_URL, normalizeRuntimeVersion } from '../../domain/pwa/runtimeVersion';

const REQUEST_TIMEOUT_MS = 10000;

/**
 * `fetch` and `AbortController` arrive as defaulted parameters, as in
 * `carContentClient`. The timeout is not optional: a request that never settles
 * would leave the check marked in-flight and block every later one behind it,
 * which is indistinguishable from an app that stopped looking.
 */
export async function fetchDeployedRuntimeVersion({
    url = VERSION_MANIFEST_URL,
    fetchImpl = globalThis.fetch,
    timeoutMs = REQUEST_TIMEOUT_MS,
} = {}) {
    if (typeof fetchImpl !== 'function') {
        throw new Error('This browser cannot fetch the deployed version manifest.');
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetchImpl(url, {
            // Both layers of cache are refused by name. `no-store` skips the
            // HTTP cache; the service worker is told to decline this URL in its
            // own fetch handler. A cached manifest could only ever report the
            // running build as current — the one answer it must never give.
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
        });

        if (!response || !response.ok) {
            throw new Error(`${url} responded ${response ? response.status : 'nothing'}`);
        }

        const manifest = normalizeRuntimeVersion(await response.json());

        // A 404 rewritten to the app shell parses as HTML, and a hosting
        // misconfiguration can serve anything at all. Neither is a manifest, and
        // treating one as a build id would reload the page every minute forever.
        if (!manifest) {
            throw new Error('The deployed version manifest is not a version manifest.');
        }

        return manifest;
    } finally {
        clearTimeout(timer);
    }
}
